import { hydrateRoot } from 'react-dom/client';
import { type Environment } from 'react-relay';
import { Provider } from 'react-redux';
import { type Store } from 'redux';

import { IntlProvider } from 'dibs-react-intl';
import serverVars from 'server-vars';

import { HeaderFooterServerVarsContextProvider } from '../components/HeaderFooterServerVarsContext/HeaderFooterServerVarsContext.tsx';
import { addLoginHandlers, initResetUpdateUserState } from '../utils/loginHandlers.ts';
import { actionCreators } from '../actions/index.ts';

type HeaderClientArgs = {
    relayEnv: Environment;
    node: Element;
    store: Store;
};

type HeaderClientResponse = {
    store: Store;
    getBuyerHeaderUiState: () => Store;
};

const getBuyerHeaderClient = async ({
    node,
    store,
    relayEnv,
}: HeaderClientArgs): Promise<HeaderClientResponse> => {
    const isDesktop = serverVars.get('settings.isDesktop');
    const isTablet = serverVars.get('settings.isTablet');
    const isMobile = serverVars.get('settings.isMobile');
    let HeaderRenderer;
    let headerRootQuery;
    let headerInitialVariables;

    if (isDesktop) {
        ({ HeaderRenderer, headerRootQuery, headerInitialVariables } = await import(
            /* webpackChunkName: "responsiveHeader" */ '../components/responsiveHeader.js'
        ));
    } else {
        ({ HeaderRenderer, headerRootQuery, headerInitialVariables } = await import(
            /* webpackChunkName: "mobileHeader" */ '../components/mobileHeader.js'
        ));
    }

    const locale = serverVars.get('locale');
    const messages = serverVars.get('messages');
    const buyerHost = serverVars.get('buyerHost');
    const isUserTypeTrade = serverVars.get('settings.isUserTypeTrade');
    const isUserTypeVip = serverVars.get('settings.isUserTypeVip');
    const isMWSearchBarRedesignVariant = serverVars.get('isMWSearchBarRedesignVariant');
    const isHeaderFullWidth = serverVars.get('isHeaderFullWidth') === 'true';

    const props = {
        initialVariables: headerInitialVariables,
        environment: relayEnv,
        query: headerRootQuery,
    };

    addLoginHandlers(() => {
        store.dispatch(actionCreators.setUpdateUserState(true));
    });
    initResetUpdateUserState(() => store.dispatch(actionCreators.setUpdateUserState(false)));
    hydrateRoot(
        node,
        <IntlProvider locale={locale} messages={messages}>
            <HeaderFooterServerVarsContextProvider
                buyerHost={buyerHost}
                isMobile={isMobile}
                isTablet={isTablet}
                isUserTypeTrade={isUserTypeTrade}
                isUserTypeVip={isUserTypeVip}
                isMWSearchBarRedesignVariant={isMWSearchBarRedesignVariant}
                isHeaderFullWidth={isHeaderFullWidth}
            >
                <Provider store={store}>
                    <HeaderRenderer {...props} />
                </Provider>
            </HeaderFooterServerVarsContextProvider>
        </IntlProvider>,
        { identifierPrefix: 'header' }
    );

    return {
        store,
        getBuyerHeaderUiState: store.getState,
    };
};

export { getBuyerHeaderClient };
